import { Clock, MessageSquare, Mic } from "lucide-react"

import logoLight from "@/assets/logos/logo-light.svg"
import { CoachAvatar } from "@/components/member-story/CoachAvatar"
import { Button } from "@/components/ui/button"

/**
 * The first thing an invited member sees. It has one job: make it obvious this
 * is a conversation, not a form, and that nothing is published without a person
 * reading it first.
 */
export function WelcomeScreen({
  coach,
  questionCount,
  publishedStoryCount,
  starting,
  onStart,
}) {
  const coachName = coach?.name
  return (
    <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col justify-between px-6 py-10">
      <div>
        <img src={logoLight} alt="Kingdom Factor" className="h-9" />

        <p className="mt-12 font-mono text-xs uppercase tracking-[0.2em] text-kf-red">
          Member Stories
        </p>
        <h1 className="mt-3 font-display text-4xl font-bold leading-tight text-zinc-900 sm:text-5xl">
          Let's tell <em className="not-italic text-kf-red">your</em> story.
        </h1>

        <div className="mt-6 flex items-start gap-4 rounded-lg border border-zinc-200 bg-white p-4">
          <CoachAvatar
            src={coach?.avatarUrl}
            initials={coach?.initials}
            name={coachName}
            size="lg"
          />
          <div className="min-w-0">
            <p className="text-base leading-relaxed text-zinc-600">
              <strong className="text-zinc-900">{coachName}</strong> asked us to
              sit down with you. We're collecting short conversations with
              members about what God is doing in their business and their peer
              group — <strong className="text-zinc-900">yours could be next</strong>.
              No forms to fill out. Just talk it through with us.
            </p>
          </div>
        </div>

        <dl className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-sm text-zinc-600">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-zinc-400" aria-hidden="true" />
            <span>About 10 minutes</span>
          </div>
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-zinc-400" aria-hidden="true" />
            <span>{questionCount} questions</span>
          </div>
          <div className="flex items-center gap-2">
            <Mic className="h-4 w-4 text-zinc-400" aria-hidden="true" />
            <span>Type or talk</span>
          </div>
        </dl>

        {publishedStoryCount > 0 && (
          <p className="mt-8 border-l-2 border-kf-red/30 pl-4 text-sm text-zinc-500">
            {publishedStoryCount.toLocaleString()}{" "}
            {publishedStoryCount === 1 ? "member has" : "members have"} shared their
            story so far.
          </p>
        )}
      </div>

      <div className="mt-12">
        <Button
          variant="gradient"
          size="lg"
          className="w-full"
          disabled={starting}
          onClick={onStart}
        >
          {starting ? "Getting started…" : "Begin your story"}
        </Button>
        <p className="mt-4 text-center text-xs leading-relaxed text-zinc-500">
          Nothing is published from this on its own. {coachName} reads and edits
          every story before it goes anywhere.
        </p>
      </div>
    </div>
  )
}
